import React, { Suspense, lazy } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { MarketingHeader } from '../components/MarketingHeader';
import { Users, Clock, Calendar, BarChart2, FileText, Bell, ArrowRight } from 'lucide-react';

const Footer = lazy(() => import('../components/Footer'));

const FEATURES = [
  {
    icon: Users,
    title: 'Complete Staff Management',
    desc: 'Manage profiles, roles, permissions, and location assignments in one place. Onboard new hires in minutes with role-based access control.',
    bullets: ['Role-based permissions', 'Multi-location assignment', 'Fast onboarding workflow'],
    img: 'https://restaff.b-cdn.net/Screenshots/Staff.png',
    alt: 'Staff management for multi-location businesses — ReStaff',
  },
  {
    icon: Clock,
    title: 'Accurate Time Tracking',
    desc: 'Employees clock in and out using a secure PIN or QR code. Managers review and approve timesheets with a full audit trail for payroll accuracy.',
    bullets: ['PIN & QR clock-in', 'Manager timesheet approval', 'Payroll-ready CSV exports'],
    img: 'https://restaff.b-cdn.net/Screenshots/Time%20Clock.png',
    alt: 'Time clock software for teams — ReStaff',
  },
  {
    icon: Calendar,
    title: 'Smart Scheduling',
    desc: 'Build weekly schedules in minutes. Match shifts to employee availability automatically and eliminate conflicts before they happen.',
    bullets: ['Conflict-free scheduling', 'Availability-based matching', 'Instant team sharing'],
    img: 'https://restaff.b-cdn.net/Screenshots/Schedule.png',
    alt: 'Employee scheduling software — ReStaff',
  },
  {
    icon: BarChart2,
    title: 'Executive Insights',
    desc: 'Visualize labor costs, attendance trends, and team performance across all your locations. Make data-backed decisions every day.',
    bullets: ['Labor cost visibility', 'Multi-location analytics', 'Risk alert engine'],
    img: 'https://restaff.b-cdn.net/Screenshots/Executive%20Insights.png',
    alt: 'Executive workforce insights and analytics — ReStaff',
  },
  {
    icon: BarChart2,
    title: 'Advanced Reporting',
    desc: 'Generate detailed hours reports and labor cost breakdowns. Export to CSV and share directly with your payroll team.',
    bullets: ['Hours & cost reports', 'CSV export ready', 'Weekly summaries'],
    img: 'https://restaff.b-cdn.net/Screenshots/Reports.png',
    alt: 'Workforce reporting and analytics — ReStaff',
  },
  {
    icon: FileText,
    title: 'Secure Document Hub',
    desc: 'Upload and distribute HR documents, policies, and compliance forms. Track employee acknowledgments to ensure nothing falls through the cracks.',
    bullets: ['Centralized HR documents', 'Acknowledgment tracking', 'Role-based access'],
    img: 'https://restaff.b-cdn.net/Screenshots/Documents.png',
    alt: 'Secure document management for businesses — ReStaff',
  },
];

export default function Features() {
  return (
    <div className="min-h-screen bg-slate-50">
      <MarketingHeader />

      <main>
        {/* Hero */}
        <section className="py-20 px-4 text-center bg-white border-b border-slate-100">
          <div className="max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-full px-4 py-1.5 mb-6">
              <Bell size={14} className="text-orange-500" />
              <span className="text-xs font-semibold text-orange-700 uppercase tracking-wide">Everything You Need</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
              All the tools your team needs,<br />
              <span className="text-orange-500">in one platform</span>
            </h1>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              ReStaff combines scheduling, time tracking, HR documents, and analytics into a single
              workforce management platform — built for any business size.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/register">
                <Button className="bg-orange-500 hover:bg-orange-600 text-white px-8">
                  Start Free Trial <ArrowRight className="ml-2" size={16} />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button variant="outline">View Pricing</Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Feature deep-dives */}
        <section className="py-20 px-4">
          <div className="max-w-6xl mx-auto space-y-24">
            {FEATURES.map((feat, idx) => {
              const Icon = feat.icon;
              const reversed = idx % 2 !== 0;
              return (
                <div
                  key={feat.title}
                  className={`grid md:grid-cols-2 gap-12 items-center ${reversed ? 'md:grid-flow-dense' : ''}`}
                >
                  <div className={reversed ? 'md:col-start-2' : ''}>
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-orange-50 text-orange-500 mb-5">
                      <Icon size={22} />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{feat.title}</h2>
                    <p className="mt-3 text-slate-600 leading-relaxed">{feat.desc}</p>
                    <ul className="mt-5 space-y-2">
                      {feat.bullets.map((b) => (
                        <li key={b} className="flex items-center gap-2 text-sm text-slate-700">
                          <span className="w-1.5 h-1.5 rounded-full bg-orange-500 flex-shrink-0" />
                          {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className={`rounded-2xl overflow-hidden shadow-lg border border-slate-100 ${reversed ? 'md:col-start-1' : ''}`}>
                    <img
                      src={feat.img}
                      alt={feat.alt}
                      className="w-full h-auto object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-4 bg-slate-900">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>
              See it all in action
            </h2>
            <p className="mt-3 text-slate-400">Start your free 30-day trial and explore every feature with your real team.</p>
            <Link to="/register" className="mt-6 inline-block">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white px-8">
                Get Started Free
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <Suspense fallback={null}>
        <Footer />
      </Suspense>
    </div>
  );
}
